import { translated } from "./localize";

export type PopupCallback = () => void;

interface Inset {
    left?: number | "auto" | undefined;
    right?: number | "auto" | undefined;
    top?: number | "auto" | undefined;
    bottom?: number | "auto" | undefined;
}

type HandleVerticalReference = "top" | "middle" | "bottom";
type HandleHorizontalReference = "left" | "center" | "right";

/** Check if the given object is a AbsolutePosition object. **/
function isInset(anchor: HTMLElement | Inset): anchor is Inset {
    return (
        typeof anchor === "object" && ("left" in anchor || "right" in anchor || "top" in anchor || "bottom" in anchor)
    );
}

// Position based on the window inset
interface InsetPosition {
    inset: Inset; // former "absPos" option
}

// Position based on the position of another element
interface AnchorElementPosition {
    anchor: HTMLElement; // former "clickElement"
    dialogHandle?: `${HandleHorizontalReference} ${HandleVerticalReference}`; // former "my" property of "position" option
    anchorHandle?: `${HandleHorizontalReference} ${HandleVerticalReference}`; // former "at" property of "position" option
}

export interface HtmlDialogParams {
    name?: string;
    title?: string;
    width?: number | "auto" | "fit-content";
    height?: number | "auto";
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
    observeDimensions?: boolean;
    position?: InsetPosition | AnchorElementPosition | null;
    onOpen?: PopupCallback; // former "open" option
    onClose?: PopupCallback; // former "close" option
    modal?: boolean;
    closeOthers?: boolean;
    closeOnEscape?: boolean;
    reloadCallback?: PopupCallback; // former "reload" option
}

function isInsetPosition(position: HtmlDialogParams["position"]): position is InsetPosition {
    return (
        typeof position === "object" &&
        "inset" in position &&
        typeof position.inset === "object" &&
        isInset(position.inset)
    );
}

function isHTMLElement(anchor: any): anchor is HTMLElement {
    return (
        anchor instanceof HTMLElement ||
        (typeof anchor === "object" && Object.getPrototypeOf(anchor.constructor).name === "HTMLElement")
    );
}

function isAnchorElementPosition(position: HtmlDialogParams["position"]): position is AnchorElementPosition {
    return typeof position === "object" && "anchor" in position && isHTMLElement(position.anchor);
}

export class HtmlDialog {
    public readonly params: HtmlDialogParams;
    private readonly onOpen: PopupCallback[];
    private readonly dialog: HTMLDialogElement;
    private readonly titleElement: HTMLHeadingElement;
    private dragState: {
        startY: number;
        startX: number;
        startDialogRect: DOMRect;
    } | null = null;

    /** Create a new popup using the HTML dialog element.
     *
     * @param element - HTML element to convert to a popup.
     *
     * @param params - Popup parameters.
     *
     * @param params.title - The title of the popup.
     *
     * @param params.name - Name of the popup. If given, the popup is registered
     * in the PopupRegistry, and it's ensures the is only a single popup with this name.
     * This allows to retrieve the popup instance later from the registry.
     *
     * @param params.width - The width (px) of the popup or "auto" or "fit-content". Default: "fit-content"
     *
     * @param params.height - The height (px) of the popup or "auto". Default: "auto"
     *
     * @param params.onOpen - Function that gets called when the popup is opened.
     *
     * @param params.onClose - Function that gets called when the popup is closed.
     *
     * @param params.position - Definition where the new dialog should be positioned.
     *
     * @param params.position.inset - InsetPosition object where the new dialog should be positioned.
     *
     * @param params.position.anchor - HTMLElement or AbsolutePosition object where the new dialog should be positioned.
     *
     * @param params.position.dialogHandle - String description of what point of the popup  is
     * positioned over the `anchor`. Only if the anchor is an HTML element. Default: "center middle"
     *
     * @param params.position.anchorHandle - String description of where the point of the popup what is
     * positioned over the `anchor`. Only if the anchor is an HTML element. Default: "left top"
     *
     * @param params.modal - Prevent uses from clicking behind the popup, by drawing a semitransparent
     * overlay behind it.
     *
     * @param params.closeOthers - Automatically close other popups with the same name. Default: true
     *
     * @param params.closeOnEscape - Close the dialog when the Esc key was pressed.
     *
     * @param params.observeDimensions - Observe the dimensions of the dialog and adjust the position
     * if for example content loading changes the height of the dialog.
     *
     */
    constructor(element: HTMLElement, params: HtmlDialogParams) {
        this.params = {
            width: "fit-content",
            modal: false,
            title: "",
            closeOthers: true,
            closeOnEscape: true,
            observeDimensions: true,
            ...params,
        };

        this.dialog = document.createElement("dialog");
        this.dialog.classList.add("html-dialog-popup");

        if (typeof this.params.width === "number") {
            this.dialog.style.width = `${this.params.width}px`;
        } else if (this.params.width) {
            this.dialog.style.width = this.params.width;
        }
        if (typeof this.params.minWidth === "number") {
            this.dialog.style.minWidth = `${this.params.minWidth}px`;
        }
        if (typeof this.params.maxWidth === "number") {
            this.dialog.style.maxWidth = `${this.params.maxWidth}px`;
        }

        if (typeof this.params.height === "number") {
            this.dialog.style.height = `${this.params.height}px`;
        } else if (this.params.height) {
            this.dialog.style.height = this.params.height;
        }
        if (typeof this.params.minHeight === "number") {
            this.dialog.style.minHeight = `${this.params.minHeight}px`;
        }
        if (typeof this.params.maxHeight === "number") {
            this.dialog.style.maxHeight = `${this.params.maxHeight}px`;
        }

        const header = document.createElement("header");
        header.addEventListener("mousedown", this.onDragStart);
        header.addEventListener("touchstart", this.onDragStart);
        document.addEventListener("mousemove", this.onDrag);
        document.addEventListener("touchmove", this.onDrag);
        document.addEventListener("mouseup", this.onDragEnd);
        document.addEventListener("touchend", this.onDragEnd);

        this.titleElement = document.createElement("h1");
        this.titleElement.title = this.params.title;
        this.titleElement.innerText = this.params.title;

        const closeButton = document.createElement("button");
        closeButton.addEventListener("click", this.close);
        closeButton.type = "button";
        closeButton.title = translated("Close");
        closeButton.ariaLabel = translated("Close");
        closeButton.classList.add("html-dialog-popup-close-button");

        header.appendChild(this.titleElement);
        header.appendChild(closeButton);
        this.dialog.appendChild(header);

        const main = document.createElement("main");
        main.appendChild(element);

        this.dialog.appendChild(main);

        this.onOpen = [];
        if (this.params.onOpen) {
            this.addOnOpen(this.params.onOpen);
        }
        if (this.params.onClose) {
            this.addOnClose(this.params.onClose);
        }
        this.dialog.addEventListener("cancel", (event) => {
            if (!this.params.closeOnEscape) {
                event.preventDefault();
            }
        });

        if (this.params.closeOthers && this.params.name) {
            registry.close(this.params.name);
        }

        document.body.append(this.dialog);
        this.dialog.addEventListener("close", () => {
            document.body.removeChild(this.dialog);
        });

        this.open();

        // establish resize observer, to ensure the dialog is always visible
        if (this.params.observeDimensions) {
            new ResizeObserver(() => {
                const dialogRect = this.dialog.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const dialogHeight = dialogRect.height;
                if (dialogHeight < windowHeight && dialogRect.top + dialogHeight > windowHeight) {
                    this.dialog.style.top = `${windowHeight - dialogHeight}px`;
                    this.dialog.style.bottom = "auto";
                }
                const windowWidth = window.innerWidth;
                const dialogWidth = dialogRect.width;
                if (dialogWidth < windowWidth && dialogRect.left + dialogWidth > windowWidth) {
                    this.dialog.style.left = `${windowWidth - dialogWidth}px`;
                    this.dialog.style.right = "auto";
                } else if (dialogWidth < windowWidth && dialogRect.left < 0) {
                    this.dialog.style.left = "0";
                    this.dialog.style.right = "auto";
                }
            }).observe(this.dialog);
        }
    }

    public get mainElement() {
        return this.dialog.querySelector("main");
    }

    /** Add a new callback, called after the popup was opened. **/
    public addOnOpen = (callback: PopupCallback) => {
        this.onOpen.push(callback);
    };

    /** Add a new callback, called after the popup was closed. **/
    public addOnClose = (callback: PopupCallback) => {
        if (typeof callback === "function") {
            this.dialog.addEventListener("close", callback);
        }
    };

    /** Remove a callback, called after the popup was closed. **/
    public removeOnClose = (callback: PopupCallback) => {
        if (typeof callback === "function") {
            this.dialog.removeEventListener("close", callback);
        }
    };

    /** Adjust the position and size of the popup
     *
     * @param position.anchor - HTMLElement or AbsolutePosition object to position the popup over.
     * @param position.dialogHandle - String describing the point on the dialog placed over the `anchor`.
     * @param position.anchorHandle - String describing the point on the `anchor` where the dialog is placed over.
     */
    public immediateReposition = (position: HtmlDialogParams["position"] = this.params.position) => {
        let dialogPosition: Inset = {
            left: "auto",
            right: "auto",
            top: 20,
            bottom: "auto",
        };

        if (isInsetPosition(position)) {
            // position is an inset
            dialogPosition = { ...dialogPosition, ...position.inset };

            // swap position left and right if the page is RTL
            if (document.querySelector("html").getAttribute("dir") === "rtl") {
                [dialogPosition.left, dialogPosition.right] = [dialogPosition.right, dialogPosition.left];
            }
        } else if (isAnchorElementPosition(position)) {
            // position is an HTMLElement
            const {
                anchor,
                anchorHandle = "middle center",
                dialogHandle = "left top",
            } = position as AnchorElementPosition;
            let anchorReferenceX = 0;
            let anchorReferenceY = 0;
            let left = 0;
            let top = 0;
            const anchorRect = anchor.getBoundingClientRect();
            const dialogRect = this.dialog.getBoundingClientRect();

            // calculate the reference point on the anchor
            const anchorHandlePosition = anchorHandle.split(" ");
            if (anchorHandlePosition.includes("top")) {
                anchorReferenceY = anchorRect.top;
            } else if (anchorHandlePosition.includes("middle")) {
                anchorReferenceY = anchorRect.top + anchorRect.height / 2;
            } else if (anchorHandlePosition.includes("bottom")) {
                anchorReferenceY = anchorRect.top + anchorRect.height;
            }
            if (anchorHandlePosition.includes("left")) {
                anchorReferenceX = anchorRect.left;
            } else if (anchorHandlePosition.includes("center")) {
                anchorReferenceX = anchorRect.left + anchorRect.width / 2;
            } else if (anchorHandlePosition.includes("right")) {
                anchorReferenceX = anchorRect.left + anchorRect.width;
            }

            // calculate the position of the popup
            const dialogHandlePosition = dialogHandle.split(" ");
            if (dialogHandlePosition.includes("top")) {
                top = anchorReferenceY;
            } else if (dialogHandlePosition.includes("middle")) {
                top = anchorReferenceY - dialogRect.height / 2;
            } else if (dialogHandlePosition.includes("bottom")) {
                top = anchorReferenceY - dialogRect.height;
            }
            if (dialogHandlePosition.includes("left")) {
                left = anchorReferenceX;
            } else if (dialogHandlePosition.includes("center")) {
                left = anchorReferenceX - dialogRect.width / 2;
            } else if (dialogHandlePosition.includes("right")) {
                left = anchorReferenceX - dialogRect.width;
            }

            dialogPosition = { top: top, right: "auto", bottom: "auto", left: left };
        }

        // use dialog width to calculate inset if left and right are "auto"
        if (dialogPosition.left === "auto" && dialogPosition.right === "auto") {
            const dialogRect = this.dialog.getBoundingClientRect();
            const windowWidth = window.innerWidth;
            const dialogWidth = dialogRect.width;
            dialogPosition.left = (windowWidth - dialogWidth) / 2;
        }

        if (typeof dialogPosition.top === "number") {
            this.dialog.style.top = `${dialogPosition.top}px`;
        } else {
            this.dialog.style.top = dialogPosition.top;
        }
        if (typeof dialogPosition.right === "number") {
            this.dialog.style.right = `${dialogPosition.right}px`;
        } else {
            this.dialog.style.right = dialogPosition.right;
        }
        if (typeof dialogPosition.bottom === "number") {
            this.dialog.style.bottom = `${dialogPosition.bottom}px`;
        } else {
            this.dialog.style.bottom = dialogPosition.bottom;
        }
        if (typeof dialogPosition.left === "number") {
            this.dialog.style.left = `${dialogPosition.left}px`;
        } else {
            this.dialog.style.left = dialogPosition.left;
        }

        // ensure the dialog is not outside the viewport
        const dialogRect = this.dialog.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const collisionMargin = "5px";
        if (dialogRect.right > windowWidth) {
            this.dialog.style.left = "auto";
            this.dialog.style.right = collisionMargin;
        }
        if (dialogRect.left < 0) {
            this.dialog.style.left = collisionMargin;
            this.dialog.style.right = "auto";
        }
        if (dialogRect.top < 0) {
            this.dialog.style.top = collisionMargin;
            this.dialog.style.bottom = "auto";
        }
    };

    /** Same as `immediateReposition` but covered in a setTimeout, to allow other code to render. **/
    public reposition = (position: HtmlDialogParams["position"] = this.params.position) => {
        window.setTimeout(() => {
            this.immediateReposition(position);
        }, 0);
    };

    public setTitle = (text: string) => {
        this.titleElement.title = text;
        this.titleElement.innerText = text;
    };

    /** Instantiate (if required) and open the popup. **/
    public open = () => {
        if (this.params.modal) {
            this.dialog.showModal();
        } else {
            this.dialog.show();
        }

        this.onOpen.forEach((callback) => {
            if (typeof callback === "function") {
                callback();
            }
        });

        // position the dialog
        this.immediateReposition();

        // self register this popup
        if (this.params.name) {
            registry.register(this, this.params.name);
        }
    };

    /** Close the popup **/
    public close = () => {
        this.dialog.close();
        registry.remove(this);
    };

    private onDragStart = (event: MouseEvent | TouchEvent) => {
        if (this.dragState) {
            // already dragging
            event.preventDefault();
            return;
        }

        // disable text selection, disable pull to refresh, enable move cursor
        this.dialog.classList.add("dragging");

        const dialogRect = this.dialog.getBoundingClientRect();
        if (window.TouchEvent && event instanceof TouchEvent) {
            this.dragState = {
                startY: event.touches[0].clientY,
                startX: event.touches[0].clientX,
                startDialogRect: dialogRect,
            };
        } else if (event instanceof MouseEvent) {
            this.dragState = {
                startX: event.clientX,
                startY: event.clientY,
                startDialogRect: dialogRect,
            };
        } else {
            // unexpected event
            return;
        }
    };

    private onDrag = (event: MouseEvent | TouchEvent) => {
        if (!this.dragState) {
            // not dragging
            return;
        }

        event.preventDefault();
        window.getSelection().removeAllRanges();

        let clientX: number;
        let clientY: number;
        if (window.TouchEvent && event instanceof TouchEvent) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else if (event instanceof MouseEvent) {
            clientX = event.clientX;
            clientY = event.clientY;
        } else {
            // unexpected event
            return;
        }

        // ignore all movements outside the viewport
        if (clientX < 0 || clientY < 0 || clientX > window.innerWidth || clientY > window.innerHeight) {
            return;
        }

        // calculate the delta from the start position
        let deltaX = clientX - this.dragState.startX;
        let deltaY = clientY - this.dragState.startY;

        // if the new position is out of screen, adjust the delta to the border
        if (this.dragState.startDialogRect.left + deltaX < 0) {
            deltaX = -this.dragState.startDialogRect.left;
        } else if (this.dragState.startDialogRect.right + deltaX > window.innerWidth) {
            deltaX = window.innerWidth - this.dragState.startDialogRect.right;
        }
        if (this.dragState.startDialogRect.top + deltaY < 0) {
            deltaY = -this.dragState.startDialogRect.top;
        } else if (this.dragState.startDialogRect.bottom + deltaY > window.innerHeight) {
            deltaY = window.innerHeight - this.dragState.startDialogRect.bottom;
        }

        // calculate the new position and move the dialog
        const newLeft = this.dragState.startDialogRect.left + deltaX;
        const newTop = this.dragState.startDialogRect.top + deltaY;
        this.dialog.style.top = `${newTop}px`;
        this.dialog.style.right = "auto";
        this.dialog.style.bottom = "auto";
        this.dialog.style.left = `${newLeft}px`;
    };

    private onDragEnd = () => {
        this.dragState = null;
        this.dialog.classList.remove("dragging");
        document.body.style.touchAction = "auto";
    };
}

class PopupRegistry {
    private readonly registry: {
        [key: string]: HtmlDialog;
    };

    private vacancyCallbacks: (() => void)[];

    constructor() {
        this.registry = {};
        this.vacancyCallbacks = [];
    }

    /** Get the named popup instance from the registry. **/
    public get = (name: string): HtmlDialog | undefined => {
        return this.registry?.[name];
    };

    /** Close the named popup instance. **/
    public close = (name: string) => {
        this.get(name)?.close();
    };

    /** Close all popup instances. **/
    public closeAll = () => {
        for (const key in this.registry) {
            this.registry[key]?.close();
        }
    };

    /** Reposition the named popup instance. **/
    public reposition = (name: string) => {
        this.get(name)?.reposition();
    };

    /** Remove the given popup instance from the registry. **/
    public remove = (popup: HtmlDialog): void => {
        for (const key in this.registry) {
            if (this.registry[key] === popup) {
                delete this.registry[key];
            }
        }
        if (Object.keys(this.registry).length === 0) {
            setTimeout(() => {
                if (Object.keys(this.registry).length === 0) {
                    this.vacancyCallbacks.forEach((callback) => {
                        callback();
                    });
                    this.vacancyCallbacks = [];
                }
            }, 300);
        }
    };

    /** Add the given popup instance to the registry if the name is not yet used. */
    public register = (popup: HtmlDialog, name: string): void => {
        if (name in this.registry) {
            throw Error(
                `Popup name ${JSON.stringify(name)} already in use. ` +
                    "Choose a different name, no name, or close the other popup before.",
            );
        } else {
            this.registry[name] = popup;
        }
    };

    /** Add a function to call after the last popup was closed. **/
    public addVacancyCallback = (callback: () => void) => {
        if (typeof callback === "function" && !this.vacancyCallbacks.includes(callback)) {
            this.vacancyCallbacks.push(callback);
        }
    };
}

const registry = new PopupRegistry();
// Currently unused, but ready when needed:
//   export const closePopup = registry.close;
//   export const repositionPopup = registry.reposition;
export const closeAllPopups = registry.closeAll;
export const addVacancyCallback = registry.addVacancyCallback;
